// ─────────────────────────────────────────────────────────────────────────────
// UserDetailModal — popup shown when a directory row is clicked. Pure
// presentation: it takes the raw `user` row, derives a view-model via
// buildUserDetail, and renders a header + tab bar + the active tab. Each tab is
// its own module (./tabs/*); adding one means adding a file and a TABS entry.
// No backend calls live here.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./UserDetailModal.module.css";
import { buildUserDetail } from "./buildUserDetail";
import ProfileTab from "./tabs/ProfileTab";
import AccessTab from "./tabs/AccessTab";
import SessionsTab from "./tabs/SessionsTab";
import ActivityTab from "./tabs/ActivityTab";
import PoliciesTab from "./tabs/PoliciesTab";

const TABS = [
  { key: "profile", label: "Profile", Component: ProfileTab },
  { key: "access", label: "Access", Component: AccessTab },
  { key: "sessions", label: "Sessions", Component: SessionsTab },
  { key: "activity", label: "Activity", Component: ActivityTab },
  { key: "policies", label: "Policies", Component: PoliciesTab },
];

export default function UserDetailModal({ user, onClose }) {
  const [active, setActive] = useState("profile");

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!user) return null;
  const detail = buildUserDetail(user);
  const ActiveTab = TABS.find((t) => t.key === active).Component;

  // Portal to <body> so no ancestor's transform/backdrop-filter can trap the
  // fixed overlay (which would break centering and let the topbar overlap it).
  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerMeta}>
            <span className={styles.headerName}>{detail.name}</span>
            <span className={styles.headerEmail}>{detail.email}</span>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <nav className={styles.tabBar}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`${styles.tabBtn} ${active === t.key ? styles.tabActive : ""}`}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className={styles.body}>
          <ActiveTab detail={detail} />
        </div>
      </div>
    </div>,
    document.body
  );
}
